import { Injectable } from '@angular/core';

export interface SteuernPerson {
  id: number;
  label: string;
}

export type PersonRole = 'first' | 'spouse' | 'child';

export interface SteuernAddress {
  street?: string;
  number?: string;
  zipCode?: string;
  place?: string;
}

export type SteuernOverviewHighlightId =
  | 'personal-information'
  | 'income'
  | 'assets'
  | 'health'
  | 'other';

export const STEUERN_STEP_ORDER: SteuernOverviewHighlightId[] = [
  'personal-information',
  'income',
  'assets',
  'health',
  'other',
];

export interface SmzhSteuernState {
  meta?: {
    taxDeclarationUploaded?: boolean;
    overviewHighlightStepId?: SteuernOverviewHighlightId;
    completedStepIds?: SteuernOverviewHighlightId[];
    voucherCode?: string;
    taxDeclarationOrderSubmitted?: boolean;
    taxDeclarationCompleted?: boolean;
    missingDocuments?: {
      entries: Array<{ name: string; uploadDocument?: string; _documentId?: string | null }>;
      notes?: string;
    };
    incomeIncludedChildIds?: string[];
    healthIncludedChildIds?: string[];
    otherIncludedChildIds?: string[];
  };
  persons: SteuernPerson[];
  personRoles: Record<string, PersonRole>;
  address?: SteuernAddress;
  pages: {
    personalInformation: Record<string, Record<string, unknown>>;
    income: Record<string, Record<string, unknown>>;
    assets: Record<string, Record<string, unknown>>;
    health: Record<string, Record<string, unknown>>;
    other: Record<string, Record<string, unknown>>;
  };
}

export interface SteuernFlowState {
  persons: SteuernPerson[];
  personRoles: Record<string, PersonRole>;
  personalInformation: Record<string, Record<string, unknown>>;
  address?: SteuernAddress;
  income: Record<string, Record<string, unknown>>;
  overviewHighlightStepId?: SteuernOverviewHighlightId;
}

function defaultState(): SmzhSteuernState {
  return {
    persons: [{ id: 1, label: 'Person 1' }],
    personRoles: { '1': 'first' },
    address: undefined,
    pages: {
      personalInformation: {},
      income: {},
      assets: {},
      health: {},
      other: {},
    },
  };
}

export function getPersonLabelFromData(
  data: Record<string, unknown> | undefined,
  personIndex: number
): string {
  if (!data) return `Person ${personIndex + 1}`;
  const first = (data['firstname'] as string)?.trim?.() ?? '';
  const last = (data['lastname'] as string)?.trim?.() ?? '';
  if (first && last) return `${first} ${last}`.trim();
  if (first) return first;
  if (last) return last;
  return `Person ${personIndex + 1}`;
}

@Injectable({ providedIn: 'root' })
export class SteuernStorageService {
  private state: SmzhSteuernState = defaultState();

  private getRawState(): SmzhSteuernState {
    return this.state;
  }

  private writeState(state: SmzhSteuernState): void {
    this.state = state;
  }

  getState(): SmzhSteuernState {
    return this.getRawState();
  }

  setState(state: SmzhSteuernState): void {
    this.writeState(state);
  }

  resetState(): void {
    this.writeState(defaultState());
  }

  getTaxDeclarationUploaded(): boolean {
    return this.getRawState().meta?.taxDeclarationUploaded === true;
  }

  setTaxDeclarationUploaded(value: boolean): void {
    const state = this.getRawState();
    state.meta = { ...state.meta, taxDeclarationUploaded: value };
    this.writeState(state);
  }

  getOverviewHighlightStepId(): SteuernOverviewHighlightId | undefined {
    return this.getRawState().meta?.overviewHighlightStepId;
  }

  setOverviewHighlightStepId(id: SteuernOverviewHighlightId | undefined): void {
    const state = this.getRawState();
    state.meta = { ...state.meta, overviewHighlightStepId: id };
    this.writeState(state);
  }

  getVoucherCode(): string {
    const value = this.getRawState().meta?.voucherCode;
    return typeof value === 'string' ? value : '';
  }

  setVoucherCode(value: string): void {
    const state = this.getRawState();
    state.meta = { ...state.meta, voucherCode: value };
    this.writeState(state);
  }

  getTaxDeclarationOrderSubmitted(): boolean {
    return this.getRawState().meta?.taxDeclarationOrderSubmitted === true;
  }

  setTaxDeclarationOrderSubmitted(value: boolean): void {
    const state = this.getRawState();
    state.meta = { ...state.meta, taxDeclarationOrderSubmitted: value };
    this.writeState(state);
  }

  getTaxDeclarationCompleted(): boolean {
    return this.getRawState().meta?.taxDeclarationCompleted === true;
  }

  setTaxDeclarationCompleted(value: boolean): void {
    const state = this.getRawState();
    state.meta = { ...state.meta, taxDeclarationCompleted: value };
    this.writeState(state);
  }

  getMissingDocuments(): { entries: Array<{ name: string; uploadDocument?: string; _documentId?: string | null }>; notes?: string } | null {
    const value = this.getRawState().meta?.missingDocuments;
    return value && typeof value === 'object' ? value : null;
  }

  setMissingDocuments(value: { entries: Array<{ name: string; uploadDocument?: string; _documentId?: string | null }>; notes?: string } | null): void {
    const state = this.getRawState();
    state.meta = { ...state.meta, missingDocuments: value ?? undefined };
    this.writeState(state);
  }

  getIncomeIncludedChildIds(): string[] {
    const ids = this.getRawState().meta?.incomeIncludedChildIds;
    return Array.isArray(ids) ? [...ids] : [];
  }

  setIncomeIncludedChildIds(ids: string[]): void {
    const state = this.getRawState();
    state.meta = { ...state.meta, incomeIncludedChildIds: ids.length ? ids : undefined };
    this.writeState(state);
  }

  getHealthIncludedChildIds(): string[] {
    const ids = this.getRawState().meta?.healthIncludedChildIds;
    return Array.isArray(ids) ? [...ids] : [];
  }

  setHealthIncludedChildIds(ids: string[]): void {
    const state = this.getRawState();
    state.meta = { ...state.meta, healthIncludedChildIds: ids.length ? ids : undefined };
    this.writeState(state);
  }

  getOtherIncludedChildIds(): string[] {
    const ids = this.getRawState().meta?.otherIncludedChildIds;
    return Array.isArray(ids) ? [...ids] : [];
  }

  setOtherIncludedChildIds(ids: string[]): void {
    const state = this.getRawState();
    state.meta = { ...state.meta, otherIncludedChildIds: ids.length ? ids : undefined };
    this.writeState(state);
  }

  getCompletedStepIds(): SteuernOverviewHighlightId[] {
    const ids = this.getRawState().meta?.completedStepIds;
    return Array.isArray(ids) ? ids : [];
  }

  markStepCompleted(stepId: SteuernOverviewHighlightId): void {
    const idx = STEUERN_STEP_ORDER.indexOf(stepId);
    if (idx < 0) return;
    const state = this.getRawState();
    const current = state.meta?.completedStepIds ?? [];
    const currentMax = current.length === 0 ? -1 : Math.max(...current.map((id) => STEUERN_STEP_ORDER.indexOf(id)));
    if (idx <= currentMax) return;
    state.meta = { ...state.meta, completedStepIds: STEUERN_STEP_ORDER.slice(0, idx + 1) };
    this.writeState(state);
  }

  isStepDataComplete(stepId: SteuernOverviewHighlightId): boolean {
    const state = this.getRawState();
    if (stepId === 'personal-information') {
      const persons = state.persons?.length ? state.persons : defaultState().persons;
      const pi = state.pages?.personalInformation ?? {};
      const addr = state.address;
      if (persons.length === 0) return false;
      const hasAddress =
        addr &&
        typeof addr === 'object' &&
        String(addr.street ?? '').trim() !== '' &&
        String(addr.zipCode ?? '').trim() !== '' &&
        String(addr.place ?? '').trim() !== '';
      if (!hasAddress) return false;
      for (const p of persons) {
        const key = String(p.id);
        const data = pi[key];
        if (!data || typeof data !== 'object') return false;
        const first = String((data['firstname'] as string) ?? '').trim();
        const last = String((data['lastname'] as string) ?? '').trim();
        if (first === '' || last === '') return false;
      }
      return true;
    }
    if (stepId === 'income') {
      const completed = this.getCompletedStepIds();
      return completed.includes('income');
    }
    const completed = this.getCompletedStepIds();
    return completed.includes(stepId);
  }

  getFirstIncompleteStepPath(): string {
    for (const id of STEUERN_STEP_ORDER) {
      if (!this.isStepDataComplete(id)) return `/steuern/${id}`;
    }
    return '/steuern/overview';
  }

  isViewOnlyStepAccessAllowed(): boolean {
    if (this.getTaxDeclarationOrderSubmitted()) return true;
    const completed = this.getCompletedStepIds();
    return STEUERN_STEP_ORDER.every((id) => completed.includes(id));
  }

  getPersons(): SteuernPerson[] {
    const state = this.getRawState();
    return state.persons.length > 0 ? state.persons : defaultState().persons;
  }

  setPersons(persons: SteuernPerson[]): void {
    const state = this.getRawState();
    state.persons = persons;
    this.writeState(state);
  }

  getPersonalInformation(): Record<string, Record<string, unknown>> {
    return this.getRawState().pages.personalInformation ?? {};
  }

  getIncome(): Record<string, Record<string, unknown>> {
    return this.getRawState().pages.income ?? {};
  }

  getAssets(): Record<string, Record<string, unknown>> {
    return this.getRawState().pages.assets ?? {};
  }

  getHealth(): Record<string, Record<string, unknown>> {
    return this.getRawState().pages.health ?? {};
  }

  persistPageData(
    pageKey: 'income' | 'assets' | 'health' | 'other',
    personsData: Record<string, Record<string, unknown>>,
    serialize: (raw: Record<string, unknown>) => Record<string, unknown>
  ): void {
    const state = this.getRawState();
    const page: Record<string, Record<string, unknown>> = {};
    for (const [id, data] of Object.entries(personsData)) {
      page[String(id)] = serialize(data) as Record<string, unknown>;
    }
    state.pages[pageKey] = page;
    this.writeState(state);
  }

  serializeHealthPersonData(raw: Record<string, unknown>): Record<string, unknown> {
    return {
      showAdditionalDocuments: raw['showAdditionalDocuments'] === true,
      _healthInsurerDeductionId: raw['_healthInsurerDeductionId'],
      _healthInsurerDocumentId: raw['_healthInsurerDocumentId'] ?? null,
      _additionalHealthInsurerDeductionId: raw['_additionalHealthInsurerDeductionId'] ?? null,
      _additionalHealthInsurerDocumentId: raw['_additionalHealthInsurerDocumentId'] ?? null,
      _saule3aDeductionId: raw['_saule3aDeductionId'],
      _saule3aDocumentId: raw['_saule3aDocumentId'] ?? null,
    };
  }

  getOther(): Record<string, Record<string, unknown>> {
    return this.getRawState().pages.other ?? {};
  }

  serializeOtherPersonData(raw: Record<string, unknown>): Record<string, unknown> {
    const out: Record<string, unknown> = { hasAdditionalDocuments: raw['hasAdditionalDocuments'] };
    const entries = raw['additionalDocumentsEntries'] as { name?: string; _additionalDocumentId?: string; _documentId?: string | null }[] | undefined;
    if (Array.isArray(entries)) {
      out['additionalDocumentsEntries'] = entries.map((e) => ({
        name: e?.name ?? '',
        _additionalDocumentId: e?._additionalDocumentId,
        _documentId: e?._documentId ?? null,
      }));
    }
    return out;
  }

  serializeIncomePersonData(raw: Record<string, unknown>): Record<string, unknown> {
    const out: Record<string, unknown> = {
      earnedMoneyThisYear: raw['earnedMoneyThisYear'],
      unemploymentBenefits: raw['unemploymentBenefits'],
      nameArbeitslosenkasse: raw['nameArbeitslosenkasse'],
      socialInsuranceBenefits: raw['socialInsuranceBenefits'],
    };
    const employers = raw['employers'] as { employerName?: string; _incomeId?: string; _documentId?: string | null }[] | undefined;
    if (Array.isArray(employers)) {
      out['employers'] = employers.map((e) => ({
        employerName: e?.employerName ?? '',
        _incomeId: e?._incomeId,
        _documentId: e?._documentId ?? null,
      }));
    }
    const socialEntries = raw['socialInsuranceEntries'] as { nameAhvPkIv?: string }[] | undefined;
    if (Array.isArray(socialEntries)) {
      out['socialInsuranceEntries'] = socialEntries.map((e) => ({ nameAhvPkIv: e?.nameAhvPkIv ?? '' }));
    }
    return out;
  }

  serializeAssetsPersonData(raw: Record<string, unknown>): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    const bankAccounts = raw['bankAccounts'] as { nameAccount?: string; _assetId?: string; _documentId?: string | null }[] | undefined;
    if (Array.isArray(bankAccounts)) {
      out['bankAccounts'] = bankAccounts.map((e) => ({
        nameAccount: e?.nameAccount ?? '',
        _assetId: e?._assetId,
        _documentId: e?._documentId ?? null,
      }));
    }
    out['otherAssets'] = raw['otherAssets'];
    const otherAssetsEntries = raw['otherAssetsEntries'] as { vermoegenswert?: string; _assetId?: string; _documentId?: string | null }[] | undefined;
    if (Array.isArray(otherAssetsEntries)) {
      out['otherAssetsEntries'] = otherAssetsEntries.map((e) => ({
        vermoegenswert: e?.vermoegenswert ?? '',
        _assetId: e?._assetId,
        _documentId: e?._documentId ?? null,
      }));
    }
    out['realEstate'] = raw['realEstate'];
    const properties = raw['properties'] as {
      taxValue?: string;
      rentalValue?: string;
      rentalIncome?: string;
      maintenanceCosts?: string;
      additionalInfo?: string;
      _propertyId?: string;
      _documentId?: string | null;
    }[] | undefined;
    if (Array.isArray(properties)) {
      out['properties'] = properties.map((p) => ({
        taxValue: p?.taxValue ?? '',
        rentalValue: p?.rentalValue ?? '',
        rentalIncome: p?.rentalIncome ?? '',
        maintenanceCosts: p?.maintenanceCosts ?? '',
        additionalInfo: p?.additionalInfo ?? '',
        _propertyId: p?._propertyId,
        _documentId: p?._documentId ?? null,
      }));
    }
    return out;
  }
}
